import { Link } from "react-router-dom";
import { useLiveMatches } from "@/hooks/useLiveMatches";
import { formatMinute } from "@/utils/format";
import { TickerSkeleton } from "@/components/common/Skeletons";
import { sortMatchesByPriority } from "@/data/priorityLeagues";

/**
 * The app's signature element: a dense, always-scrolling strip of every live
 * score, styled like a broadcast lower-third / exchange ticker. Duplicated
 * once so the CSS marquee loop (translateX -50%) is seamless.
 */
export function LiveTicker() {
  const { matches, isLoading } = useLiveMatches();

  if (isLoading && matches.length === 0) return <TickerSkeleton />;
  if (matches.length === 0) return null;

  // Unlike the Live/Schedule pages, the ticker never hides minor
  // competitions — it's a supplementary overview, not the primary content
  // area — but major leagues still lead the scroll rather than appearing
  // wherever the API happened to return them.
  const sorted = sortMatchesByPriority(matches);
  const items = [...sorted, ...sorted];

  return (
    <div className="relative h-9 bg-base-800/60 border-y border-white/5 overflow-hidden">
      <div className="flex items-center h-full whitespace-nowrap animate-ticker hover:[animation-play-state:paused]">
        {items.map((m, i) => (
          <Link
            key={`${m.id}-${i}`}
            to={`/match/${m.id}`}
            state={{ prefetchedMatch: m }}
            className="flex items-center gap-2 px-5 border-r border-white/5 text-xs shrink-0 hover:bg-white/5 transition-colors h-full"
          >
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-live animate-pulse-live" />
            </span>
            <span className="text-ink-500 font-medium">{m.league.shortName}</span>
            <span className="text-ink-100">{m.homeTeam.shortName}</span>
            <span className="font-mono font-semibold text-emerald tabular-nums">
              {m.homeScore}–{m.awayScore}
            </span>
            <span className="text-ink-100">{m.awayTeam.shortName}</span>
            <span className="font-mono text-ink-500">{formatMinute(m)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
